import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../../../components/Button";
import { useTranslation } from "../../../i18n/TranslationProvider";

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const GENDER_LABEL_KEYS = {
    Male: "onboarding.genderMale",
    Female: "onboarding.genderFemale",
    Other: "onboarding.other",
};

const INITIAL_FORM = {
    first_name: "",
    last_name: "",
    dob: "",
    gender: "",
    email: "",
    phone: "",
    school: "",
    graduation_year: "",
    sport: "",
    position: "",
    previous_concussions: "",
};

// --- MOCK: replace with real fetch once backend is ready ---
const mockValidateToken = (token) => {
    if (token === "invalid") return Promise.resolve({ valid: false });
    return Promise.resolve({ valid: true, coach_name: "Mikayla Hubbard" });
};

const mockSubmitAthlete = (token, formData) => {
    console.log("Submitting athlete for token:", token, formData);
    return new Promise((resolve) => setTimeout(() => resolve({ success: true }), 800));
};
// -----------------------------------------------------------

const AthleteSignupForm = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [tokenStatus, setTokenStatus] = useState("loading"); // "loading" | "valid" | "invalid"
    const [coachName, setCoachName] = useState("");
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");

    const [consentGiven, setConsentGiven] = useState(false);
    const [adultConsentGiven, setAdultConsentGiven] = useState(false);

    useEffect(() => {
        const validateToken = async () => {
            try {
                // TODO: replace with:
                // const res = await fetch(`/api/athlete-signup/${token}/validate`);
                // const data = await res.json();
                const data = await mockValidateToken(token);
                if (data.valid) {
                    setTokenStatus("valid");
                    setCoachName(data.coach_name);
                } else {
                    setTokenStatus("invalid");
                }
            } catch {
                setTokenStatus("invalid");
            }
        };
        validateToken();
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.first_name.trim()) newErrors.first_name = t("athleteSignup.firstNameRequired");
        if (!form.last_name.trim()) newErrors.last_name = t("athleteSignup.lastNameRequired");
        if (!form.dob) newErrors.dob = t("athleteSignup.dobRequired");
        if (!form.school.trim()) newErrors.school = t("athleteSignup.schoolRequired");
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validate();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setSubmitting(true);
        setServerError("");
        try {
            // TODO: replace with:
            // const res = await fetch(`/api/athlete-signup/${token}`, {
            //     method: "POST",
            //     headers: { "Content-Type": "application/json" },
            //     body: JSON.stringify(form),
            // });
            // const data = await res.json();
            // if (!data.success) throw new Error();
            await mockSubmitAthlete(token, form);
            setSubmitted(true);
        } catch {
            setServerError(t("auth.loginErrorGeneric"));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Loading state ──
    if (tokenStatus === "loading") {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-gray-500 text-sm">{t("athleteSignup.verifyingLink")}</p>
            </div>
        );
    }

    // ── Invalid token ──
    if (tokenStatus === "invalid") {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-sm">
                    <p className="text-gray-800 font-medium mb-2">{t("athleteSignup.invalidLinkHeading")}</p>
                    <p className="text-sm text-gray-500">
                        {t("athleteSignup.invalidLinkSubtext")}
                    </p>
                    <p
                        className="text-sm text-teal-600 cursor-pointer mt-4"
                        onClick={() => navigate("/help")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                navigate("/help");
                            }
                        }}
                    >
                        {t("athleteSignup.needHelpContactUs")}
                    </p>
                </div>
            </div>
        );
    }

    // ── Success state ──
    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4">
                <div className="text-center max-w-sm">
                    <p className="text-teal-600 font-medium mb-2">{t("athleteSignup.allSetHeading")}</p>
                    <p className="text-sm text-gray-500">
                        {t("athleteSignup.submittedSubtext", { coachName })}
                    </p>
                </div>
            </div>
        );
    }

    // ── Form ──
    return (
          <div className="custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2 ">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-sm p-8">
                {/* Header */}
                <div className="mb-6 text-center">
                    <p className="text-xl font-semibold text-gray-900">{t("athleteSignup.formTitle")}</p>
                    <h1 className=" text-sm text-gray-500 mb-1">
                        <span  className="text-teal-600" >{coachName} </span>{t("athleteSignup.invitedBy")}
                    </h1>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">

                        {/* First Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("onboarding.firstNameLabel")} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="first_name"
                                value={form.first_name}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary
                                    ${errors.first_name ? "border-red-400" : "border-gray-300"}`}
                            />
                            {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>}
                        </div>

                        {/* Last Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("onboarding.lastNameLabel")} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="last_name"
                                value={form.last_name}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary
                                    ${errors.last_name ? "border-red-400" : "border-gray-300"}`}
                            />
                            {errors.last_name && <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>}
                        </div>

                        {/* Date of Birth */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("onboarding.dobLabel")} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="dob"
                                value={form.dob}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary
                                    ${errors.dob ? "border-red-400" : "border-gray-300"}`}
                            />
                            {errors.dob && <p className="text-xs text-red-500 mt-1">{errors.dob}</p>}
                        </div>

                        {/* Gender */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("onboarding.genderLabel")}
                            </label>
                            <select
                                name="gender"
                                value={form.gender}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                            >
                                <option value="">{t("athleteSignup.selectOption")}</option>
                                {GENDER_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>{t(GENDER_LABEL_KEYS[opt])}</option>
                                ))}
                            </select>
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("auth.email")}
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("athletes.phoneLabel")}
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                value={form.phone}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        {/* School */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("athletes.schoolLabel")} <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="school"
                                value={form.school}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary
                                    ${errors.school ? "border-red-400" : "border-gray-300"}`}
                            />
                            {errors.school && <p className="text-xs text-red-500 mt-1">{errors.school}</p>}
                        </div>

                        {/* Graduation Year */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("athletes.gradYearLabel")}
                            </label>
                            <input
                                type="number"
                                name="graduation_year"
                                value={form.graduation_year}
                                onChange={handleChange}
                                min="2020"
                                max="2040"
                                placeholder={t("athleteSignup.gradYearPlaceholder")}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        {/* Sport */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("athletes.sportLabel")}
                            </label>
                            <input
                                type="text"
                                name="sport"
                                value={form.sport}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        {/* Position */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("athletes.positionLabel")}
                            </label>
                            <input
                                type="text"
                                name="position"
                                value={form.position}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>

                        {/* Previous Concussions — full width */}
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t("athletes.previousConcussionsLabel")}
                            </label>
                            <input
                                type="text"
                                name="previous_concussions"
                                value={form.previous_concussions}
                                onChange={handleChange}
                                placeholder={t("athleteSignup.concussionsPlaceholder")}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>


                    </div>


                    {/* Consent checkbox */}
                    <div className="mt-6 flex items-start">
                        <input
                            id="legal-consent"
                            type="checkbox"
                            checked={consentGiven}
                            onChange={(e) => setConsentGiven(e.target.checked)}
                            disabled={submitting}
                            className="mr-2 mt-1"
                            required
                        />
                        <label htmlFor="legal-consent" className="text-gray-700 text-sm">
                            {t("athleteSignup.consentText")}
                        </label>
                    </div>
                    {/* Legal adult consent */}
                    <div className="mt-4 flex items-start">
                        <input
                            id="legal-adult"
                            type="checkbox"
                            checked={adultConsentGiven}
                            onChange={(e) => setAdultConsentGiven(e.target.checked)}
                            disabled={submitting}
                            className="mr-2 mt-1"
                            required
                        />
                        <label htmlFor="legal-adult" className="text-gray-700 text-sm">
                            {t("athleteSignup.adultConsentText")}
                        </label>
                    </div>

                    {serverError && (
                        <p className="text-sm text-red-600 mt-4">{serverError}</p>
                    )}

                    <div className="mt-6">
                        <Button
                            type="submit"
                            disabled={submitting}
                            isLoading={submitting}
                            className="w-full"
                        >
                            {submitting ? t("athleteSignup.submitting") : t("athleteSignup.submit")}
                        </Button>
                        <p className="text-xs text-gray-400 text-center mt-3">
                            {t("athleteSignup.requiredFieldsPrefix")} <span className="text-red-500">*</span> {t("athleteSignup.requiredFieldsSuffix")}
                        </p>
                        <p
                            className="text-xs text-teal-600 text-center mt-2 cursor-pointer"
                            onClick={() => navigate("/help")}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    navigate("/help");
                                }
                            }}
                        >
                            {t("athleteSignup.needHelpContactUs")}
                        </p>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AthleteSignupForm;